import { Box, BoxProps, Flex, VStack } from '@chakra-ui/react'
import { ScrollToOptions, useVirtualizer } from '@tanstack/react-virtual'
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useDebounce } from 'react-use'
import { ScrollArea, ScrollbarOptions } from '~/components/shared/ScrollArea'
import { ScrollToIndexParams } from '~/components/shared/ScrollingList'
import { Size } from '~/types/ThemeTypes'

import { LoadingSpinner } from './LoadingSpinner'

const END_REACHED_COOLDOWN_MS = 100

export enum ScrollToIndexType {
  Group = 'group',
  Item = 'item',
}

export interface GroupedScrollToIndexParams extends ScrollToIndexParams {
  type: ScrollToIndexType
}

type GroupedScrollingListProps<TGroupData, TItemData> = BoxProps & {
  groupItemData: Map<TGroupData, TItemData[]>
  loadThresholdPercentage?: number
  scrollToIndex?: {
    index: number
    type: ScrollToIndexType
    options?: ScrollToOptions
  }
  selectedItemIndex?: number
  onBuildGroupContent: (
    groupIndex: number,
    data: TGroupData,
    isSticky: boolean,
  ) => ReactNode
  onBuildItemContent: (
    itemIndex: number,
    data: TItemData,
    isSelected: boolean,
  ) => ReactNode
  onEndReached?: () => void
  scrollbarOptions?: ScrollbarOptions
  showLoaderFooter?: boolean
  itemSpacing?: string
}

const getGroupCounts = <TGroupData, TItemData>(
  map: Map<TGroupData, TItemData[]>,
) => {
  const counts: number[] = []
  map.forEach((value) => counts.push(value.length))

  return counts
}

const getGroupData = <TGroupData, TItemData>(
  map: Map<TGroupData, TItemData[]>,
) => {
  const groupKeys = map.keys()
  return [...groupKeys]
}

const getGroupItemRenderData = <TGroupData,>(
  groupCounts: number[],
  groupData: TGroupData[],
) => {
  const indices: { groupValue: TGroupData; renderIndex: number }[] = []
  let index = 0

  for (let i = 0; i < groupCounts.length; i++) {
    indices.push({
      groupValue: groupData[i],
      renderIndex: index,
    })
    index += groupCounts[i] + 1
  }

  return indices
}

const buildFlatGroupItemRenderData = <TItemData, TGroupData>(
  groupItemData: Map<TGroupData, TItemData[]>,
) => {
  const collection: {
    isGroup: boolean
    data: TItemData | TGroupData
    actualIndex: number
  }[] = []

  const groupCounts = getGroupCounts(groupItemData)
  const groupData = getGroupData(groupItemData)
  const groupItemRenderData = getGroupItemRenderData(groupCounts, groupData)
  const stickyIndices: number[] = []

  //iterate through the map to build a flat collection
  let groupIndex = 0
  let itemIndex = 0
  for (const [group, items] of groupItemData) {
    collection.push({
      isGroup: true,
      data: group,
      actualIndex: groupIndex++,
    })

    stickyIndices.push(collection.length - 1)

    const itemData = items.map((item) => ({
      isGroup: false,
      data: item,
      actualIndex: itemIndex++,
    }))
    collection.push(...itemData)
  }

  return {
    groupCounts,
    groupData,
    groupItemRenderData,
    stickyIndices,
    flatCollection: collection,
  }
}

const LoaderFooter = () => {
  return (
    <Flex justifyContent="center" alignItems="center" height="32px">
      <LoadingSpinner size={Size.sm} />
    </Flex>
  )
}

export const GroupedScrollingList = <TGroupData, TItemData>(
  props: GroupedScrollingListProps<TGroupData, TItemData>,
) => {
  const {
    groupItemData,
    scrollToIndex,
    selectedItemIndex,
    onBuildGroupContent,
    onBuildItemContent,
    onEndReached,
    loadThresholdPercentage = 0.1,
    scrollbarOptions = {
      hidden: false,
      showOnHoverOnly: false,
    },
    showLoaderFooter = true,
    itemSpacing,
    ...rest
  } = props
  const [endReached, setEndReached] = useState<{ reached: boolean }>()
  useDebounce(
    () => {
      if (endReached?.reached === true) {
        onEndReached?.()
        setEndReached({ reached: false })
      }
    },
    END_REACHED_COOLDOWN_MS,
    [endReached],
  )

  const scrollAreaRef = useRef<HTMLDivElement>(null)

  const { groupItemRenderData, stickyIndices, flatCollection } = useMemo(() => {
    return buildFlatGroupItemRenderData(groupItemData)
  }, [groupItemData])

  const getNearestStickyGroupIndex = (flatIndex: number) => {
    const nearest = stickyIndices.findLast((index) => flatIndex >= index)

    return nearest
  }

  const virtualizer = useVirtualizer({
    count: flatCollection.length,
    estimateSize: () => 100,
    getScrollElement: () => scrollAreaRef.current,
  })

  const virtualSize = virtualizer.getTotalSize()
  const virtualItems = virtualizer.getVirtualItems()
  const nearestStickyGroupIndex = getNearestStickyGroupIndex(
    virtualizer.range?.startIndex ?? 0,
  )

  const stickyHeaderRef = useRef<HTMLDivElement>(null)
  const stickyHeader = useMemo(() => {
    if (nearestStickyGroupIndex === undefined) {
      return undefined
    }

    const group = flatCollection[nearestStickyGroupIndex]
    const groupIndex = group?.actualIndex
    const groupContent = onBuildGroupContent(
      groupIndex,
      group.data as TGroupData,
      true,
    )

    return (
      <Flex
        alignSelf="flex-start"
        key={`sticky-{groupIndex}`}
        ref={stickyHeaderRef}
        position="sticky"
        zIndex={1}
        width="100%"
        height="fit-content"
        top={0}
        bg="white"
        className="mango-list-item"
      >
        {groupContent}
      </Flex>
    )
  }, [virtualItems])

  const items = virtualItems
    .sort((a, b) => a.index - b.index)
    .map((virtualItem) => {
      const flatItem = flatCollection[virtualItem.index]
      const isGroup = flatItem.isGroup
      const isStickyGroup =
        isGroup && nearestStickyGroupIndex === virtualItem.index

      const start =
        virtualItem.start - (stickyHeaderRef.current?.clientHeight ?? 0)

      const isItemSelected = selectedItemIndex === flatItem.actualIndex

      const content = isGroup
        ? onBuildGroupContent(
            flatItem.actualIndex,
            flatItem.data as TGroupData,
            false,
          )
        : onBuildItemContent(
            flatItem.actualIndex,
            flatItem.data as TItemData,
            isItemSelected,
          )

      return (
        <Flex
          key={virtualItem.key}
          visibility={isStickyGroup ? 'hidden' : 'visible'}
          data-index={virtualItem.index}
          position={'absolute'}
          transform={`translateY(${start}px)`}
          ref={virtualizer.measureElement}
          width="100%"
          height="fit-content"
          zIndex={isGroup ? 2 : 0}
          className="mango-list-item"
        >
          {content}
        </Flex>
      )
    })

  useEffect(() => {
    const lastItem = virtualItems[virtualItems.length - 1]
    if (!lastItem) {
      return
    }

    const loadThreshold = flatCollection.length * loadThresholdPercentage

    const isNearEnd = lastItem.index >= flatCollection.length - loadThreshold

    if (isNearEnd) {
      setEndReached({ reached: true })
    }
  }, [virtualItems])

  const scrollToGroupIndex = (
    groupIndex: number,
    options?: ScrollToOptions,
  ) => {
    const group = groupItemRenderData[groupIndex]
    const renderIndex = group.renderIndex
    virtualizer.scrollToIndex(renderIndex, options)
  }

  const scrollToItemIndex = (itemIndex: number, options?: ScrollToOptions) => {
    const renderIndex = flatCollection.findIndex(
      (item) => item.actualIndex === itemIndex,
    )
    // virtualizer.scrollToIndex(renderIndex, options)
    const offset = virtualizer.getOffsetForIndex(
      renderIndex,
      options?.align ?? 'start',
    )
    if (offset === undefined) {
      return
    }
    const offsetStickyHeaderPadding =
      offset[0] - (stickyHeaderRef.current?.clientHeight ?? 0)
    virtualizer.scrollToOffset(offsetStickyHeaderPadding, options)
  }

  useEffect(() => {
    if (scrollToIndex?.type === 'group') {
      scrollToGroupIndex(scrollToIndex.index, scrollToIndex.options)
    }

    if (scrollToIndex?.type === 'item') {
      scrollToItemIndex(scrollToIndex.index, scrollToIndex.options)
    }
  }, [scrollToIndex])

  return (
    <Flex {...rest}>
      <Flex zIndex={0} width="100%">
        <ScrollArea
          flex="1"
          height="100%"
          ref={scrollAreaRef}
          overflow="auto"
          position="relative"
          scrollbarOptions={scrollbarOptions}
        >
          <VStack align="stretch" spacing=".25rem" width="100%">
            <Box
              height={`${virtualSize}px`}
              __css={{
                '*.mango-list-item+*.mango-list-item': { mt: itemSpacing },
              }}
            >
              {stickyHeader}
              {items}
            </Box>
            {showLoaderFooter && <LoaderFooter />}
          </VStack>
        </ScrollArea>
      </Flex>
    </Flex>
  )
}
